/**
 * LockedIn sport theme registry — Sprint 12 WI-2 foundation.
 *
 * Single mapping: sport key → icon id + accent tokens + display label.
 * Presentation only — no lifecycle, wallet, or backend authority.
 *
 * Accent tokens use the ratified LockedIn sport palette (WI-2). Consumers
 * `sportBadges.js` and `gameTicketStyles.js` re-export from this registry (WI-3).
 */
import { H2H_SPORTS } from './h2hSports';

/** @typedef {'basketball'|'football'|'soccer'|'baseball'|'hockey'|'boxing'|'mma'|'college'|'generic'} SportIconId */

/**
 * @typedef {Object} SportAccentTokens
 * @property {string} icon — Tailwind text color for SportIcon tint
 * @property {string} badge — pill/badge surface classes
 * @property {string} border — card/row border accent
 * @property {string} ring — focus/highlight ring
 * @property {string} initials — TeamCrest-compatible fill (WI-3 migration)
 * @property {string} tabActive — sport filter pill active state
 * @property {string} tabInactive — sport filter pill inactive state
 * @property {string} cardBadge — matchup card sport pill (no typography.base prefix)
 * @property {string} glow — featured card neon shadow
 * @property {string} stripe — compact-row left gradient
 * @property {string} label — sport label text tint
 * @property {string} rowHover — compact-row hover glow
 */

const TAB_BASE =
  'px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border transition-colors';

const BADGE_BASE =
  'text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 border';

/** @type {Record<string, { key: string, label: string, iconId: SportIconId, accent: SportAccentTokens }>} */
export const SPORT_THEMES = Object.freeze({
  NBA: {
    key: 'NBA',
    label: 'NBA',
    iconId: 'basketball',
    accent: {
      icon: 'text-orange-400',
      badge: `${BADGE_BASE} bg-orange-500/10 border-orange-500/40 text-orange-400`,
      border: 'border-orange-500/35',
      ring: 'ring-orange-500/20',
      initials: 'bg-orange-500/20 border-orange-500/35 text-orange-200',
      tabActive: `${TAB_BASE} bg-orange-500/25 text-orange-200 border-orange-500/50 shadow-[0_8px_20px_rgba(249,115,22,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-orange-500/50 hover:text-orange-400`,
      cardBadge: 'bg-orange-500/15 border-orange-500/40 text-orange-300',
      glow: 'shadow-[0_0_24px_rgba(249,115,22,0.18),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-orange-400/90 to-orange-600/40',
      label: 'text-orange-400/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(249,115,22,0.12)] hover:border-orange-500/25',
    },
  },
  NFL: {
    key: 'NFL',
    label: 'NFL',
    iconId: 'football',
    accent: {
      icon: 'text-red-500',
      badge: `${BADGE_BASE} bg-red-600/10 border-red-600/40 text-red-400`,
      border: 'border-red-600/35',
      ring: 'ring-red-600/20',
      initials: 'bg-red-600/20 border-red-600/35 text-red-200',
      tabActive: `${TAB_BASE} bg-red-600/25 text-red-200 border-red-600/50 shadow-[0_8px_20px_rgba(220,38,38,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-red-600/50 hover:text-red-400`,
      cardBadge: 'bg-red-600/15 border-red-600/40 text-red-300',
      glow: 'shadow-[0_0_24px_rgba(220,38,38,0.18),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-red-500/90 to-red-700/40',
      label: 'text-red-500/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(220,38,38,0.12)] hover:border-red-600/25',
    },
  },
  MLB: {
    key: 'MLB',
    label: 'MLB',
    iconId: 'baseball',
    accent: {
      icon: 'text-blue-500',
      badge: `${BADGE_BASE} bg-blue-600/10 border-blue-600/40 text-blue-400`,
      border: 'border-blue-600/35',
      ring: 'ring-blue-600/20',
      initials: 'bg-blue-600/20 border-blue-600/35 text-blue-200',
      tabActive: `${TAB_BASE} bg-blue-600/25 text-blue-200 border-blue-600/50 shadow-[0_8px_20px_rgba(37,99,235,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-blue-600/50 hover:text-blue-400`,
      cardBadge: 'bg-blue-600/15 border-blue-600/40 text-blue-300',
      glow: 'shadow-[0_0_24px_rgba(37,99,235,0.22),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-blue-400/90 to-blue-700/40',
      label: 'text-blue-500/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(37,99,235,0.14)] hover:border-blue-600/28',
    },
  },
  NHL: {
    key: 'NHL',
    label: 'NHL',
    iconId: 'hockey',
    accent: {
      icon: 'text-sky-400',
      badge: `${BADGE_BASE} bg-sky-500/10 border-sky-400/40 text-sky-300`,
      border: 'border-sky-400/35',
      ring: 'ring-sky-400/20',
      initials: 'bg-sky-500/20 border-sky-400/35 text-sky-100',
      tabActive: `${TAB_BASE} bg-sky-500/25 text-sky-200 border-sky-400/50 shadow-[0_8px_20px_rgba(56,189,248,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-sky-400/50 hover:text-sky-300`,
      cardBadge: 'bg-sky-500/15 border-sky-400/40 text-sky-200',
      glow: 'shadow-[0_0_24px_rgba(56,189,248,0.2),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-sky-300/90 to-cyan-400/40',
      label: 'text-sky-300/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(56,189,248,0.12)] hover:border-sky-400/25',
    },
  },
  SOCCER: {
    key: 'SOCCER',
    label: 'Soccer',
    iconId: 'soccer',
    accent: {
      icon: 'text-emerald-400',
      badge: `${BADGE_BASE} bg-emerald-500/10 border-emerald-500/40 text-emerald-400`,
      border: 'border-emerald-500/35',
      ring: 'ring-emerald-500/20',
      initials: 'bg-emerald-500/20 border-emerald-500/35 text-emerald-200',
      tabActive: `${TAB_BASE} bg-emerald-500/25 text-emerald-200 border-emerald-500/50 shadow-[0_8px_20px_rgba(16,185,129,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-emerald-500/50 hover:text-emerald-400`,
      cardBadge: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
      glow: 'shadow-[0_0_24px_rgba(16,185,129,0.18),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-emerald-400/90 to-green-600/40',
      label: 'text-emerald-400/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(16,185,129,0.12)] hover:border-emerald-500/25',
    },
  },
  MMA: {
    key: 'MMA',
    label: 'MMA',
    iconId: 'mma',
    accent: {
      icon: 'text-violet-400',
      badge: `${BADGE_BASE} bg-violet-500/10 border-violet-500/40 text-violet-400`,
      border: 'border-violet-500/35',
      ring: 'ring-violet-500/20',
      initials: 'bg-violet-500/20 border-violet-500/35 text-violet-200',
      tabActive: `${TAB_BASE} bg-violet-500/25 text-violet-200 border-violet-500/50 shadow-[0_8px_20px_rgba(139,92,246,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-violet-500/50 hover:text-violet-400`,
      cardBadge: 'bg-violet-500/15 border-violet-500/40 text-violet-300',
      glow: 'shadow-[0_0_24px_rgba(139,92,246,0.2),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-violet-400/90 to-red-500/40',
      label: 'text-violet-400/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(139,92,246,0.12)] hover:border-violet-500/25',
    },
  },
  BOXING: {
    key: 'BOXING',
    label: 'Boxing',
    iconId: 'boxing',
    accent: {
      icon: 'text-red-500',
      badge: `${BADGE_BASE} bg-red-500/10 border-red-500/40 text-red-400`,
      border: 'border-red-500/40',
      ring: 'ring-red-500/25',
      initials: 'bg-red-500/20 border-red-500/35 text-red-200',
      tabActive: `${TAB_BASE} bg-red-500/25 text-red-200 border-red-500/50 shadow-[0_8px_20px_rgba(239,68,68,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-red-500/50 hover:text-red-400`,
      cardBadge: 'bg-red-500/15 border-red-500/40 text-red-300',
      glow: 'shadow-[0_0_24px_rgba(239,68,68,0.22),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-red-400/90 to-rose-600/40',
      label: 'text-red-400/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(239,68,68,0.14)] hover:border-red-500/28',
    },
  },
  NCAA: {
    key: 'NCAA',
    label: 'College',
    iconId: 'college',
    accent: {
      icon: 'text-indigo-400',
      badge: `${BADGE_BASE} bg-indigo-500/10 border-indigo-500/40 text-indigo-400`,
      border: 'border-indigo-500/30',
      ring: 'ring-indigo-500/15',
      initials: 'bg-indigo-500/18 border-indigo-500/30 text-indigo-200',
      tabActive: `${TAB_BASE} bg-indigo-500/25 text-indigo-200 border-indigo-500/50 shadow-[0_8px_20px_rgba(99,102,241,0.25)]`,
      tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-indigo-500/50 hover:text-indigo-400`,
      cardBadge: 'bg-indigo-500/12 border-indigo-500/35 text-indigo-300',
      glow: 'shadow-[0_0_24px_rgba(99,102,241,0.15),0_8px_32px_rgba(0,0,0,0.4)]',
      stripe: 'from-indigo-400/90 to-indigo-600/40',
      label: 'text-indigo-400/90',
      rowHover: 'hover:shadow-[0_4px_18px_rgba(99,102,241,0.1)] hover:border-indigo-500/22',
    },
  },
});

const GENERIC_THEME = Object.freeze({
  key: 'GENERIC',
  label: 'Game',
  iconId: 'generic',
  accent: {
    icon: 'text-emerald-400',
    badge: `${BADGE_BASE} bg-black/50 border-gray-800 text-gray-400`,
    border: 'border-emerald-500/30',
    ring: 'ring-emerald-500/15',
    initials: 'bg-emerald-500/18 border-emerald-500/30 text-emerald-200',
    tabActive: `${TAB_BASE} bg-emerald-500 text-black border-emerald-400`,
    tabInactive: `${TAB_BASE} bg-black/40 border-gray-800 text-gray-400 hover:border-emerald-500/60 hover:text-emerald-300`,
    cardBadge: 'bg-emerald-500/12 border-emerald-500/35 text-emerald-300',
    glow: 'shadow-[0_0_20px_rgba(16,185,129,0.12),0_8px_32px_rgba(0,0,0,0.4)]',
    stripe: 'from-emerald-400/90 to-cyan-500/40',
    label: 'text-emerald-400/90',
    rowHover: 'hover:shadow-[0_4px_18px_rgba(16,185,129,0.1)] hover:border-emerald-500/22',
  },
});

/** Normalize raw sport strings to uppercase registry keys. */
export function normalizeSportKey(sport) {
  const raw = String(sport || '').trim().toUpperCase();
  return raw || null;
}

/** @returns {typeof SPORT_THEMES[string]} */
export function getSportTheme(sport) {
  const key = normalizeSportKey(sport);
  if (key && SPORT_THEMES[key]) return SPORT_THEMES[key];
  return GENERIC_THEME;
}

export function getSportLabel(sport) {
  return getSportTheme(sport).label;
}

export function getSportIconId(sport) {
  return getSportTheme(sport).iconId;
}

export function getSportIconColorClass(sport) {
  return getSportTheme(sport).accent.icon;
}

/** Matchup card neon accent set — shape preserved for gameTicketStyles consumers. */
const neonAccentCache = new Map();

export function getSportNeonAccent(sport) {
  const key = normalizeSportKey(sport);
  const cacheKey = key && SPORT_THEMES[key] ? key : '__GENERIC__';
  if (!neonAccentCache.has(cacheKey)) {
    const { accent } = cacheKey === '__GENERIC__' ? GENERIC_THEME : SPORT_THEMES[cacheKey];
    neonAccentCache.set(
      cacheKey,
      Object.freeze({
        border: accent.border,
        glow: accent.glow,
        ring: accent.ring,
        badge: accent.cardBadge,
        initials: accent.initials,
      }),
    );
  }
  return neonAccentCache.get(cacheKey);
}

/** All registered H2H sport themes in canonical order. */
export function listH2HSportThemes() {
  return H2H_SPORTS.map((key) => SPORT_THEMES[key]).filter(Boolean);
}

export default SPORT_THEMES;
